import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, Container } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { Link as RouterLink } from 'react-router-dom';
import { HomeContent, contentService } from '../services/content';
import { useTenant } from '../contexts/TenantContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useSubmissionWindow } from '../hooks/useSubmissionWindow';

const DynamicHeroBanner: React.FC = () => {
  const { tenantConfig } = useTenant();
  const { language, t } = useLanguage();
  const { isClosed: submissionsClosed } = useSubmissionWindow();
  const [homeContent, setHomeContent] = useState<HomeContent | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadContent = async () => {
      setIsLoading(true);
      try {
        const content = await contentService.getHomeContent(tenantConfig?.tenant_id, language);
        setHomeContent(content);
      } catch (error) {
        console.error('Failed to load home content:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadContent();
  }, [tenantConfig?.tenant_id, language]);

  if (isLoading || !homeContent) {
    return (
      <Box
        sx={{
          height: { xs: '300px', md: '400px' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f5f5f5'
        }}
      >
        <Typography>{t.common.loading}</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: 'relative',
        height: { xs: '300px', md: '400px' },
        overflow: 'hidden',
        backgroundImage: {
          xs: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.7)), url(${homeContent.hero.backgroundImage.src.replace('.webp', '_mobile.webp')})`,
          md: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.7)), url(${homeContent.hero.backgroundImage.src})`
        },
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        marginBottom: 4,
        display: 'flex',
        alignItems: 'center',
        '&::before': {
          content: '""',
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '60px',
          background: 'linear-gradient(transparent, rgba(245, 245, 245, 1))',
          zIndex: 1
        }
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
        <Box sx={{ maxWidth: 'md', color: 'white' }}>
          <Typography 
            variant="h2" 
            component="h1" 
            sx={{ 
              fontWeight: 'bold',
              mb: 2,
              fontSize: { xs: '2rem', sm: '3rem', md: '3.75rem' },
              textShadow: '2px 2px 4px rgba(0,0,0,0.5)'
            }}
          >
            {homeContent.hero.title}
          </Typography>
          <Typography 
            variant="h5" 
            sx={{ 
              mb: 4,
              maxWidth: '700px',
              lineHeight: 1.4,
              textShadow: '1px 1px 2px rgba(0,0,0,0.5)'
            }}
          >
            {homeContent.hero.subtitle}
          </Typography>
          {!submissionsClosed && (
            <Button 
              variant="contained" 
              size="large" 
              component={RouterLink} 
              to={homeContent.hero.button.link}
              startIcon={<AddIcon />}
              sx={{ 
                fontWeight: 'bold',
                py: 1.5,
                px: 3,
                fontSize: '1.1rem',
                background: 'linear-gradient(45deg, #FFD700, #FFC107)',
                color: 'rgba(0,0,0,0.87)',
                boxShadow: '0 4px 10px rgba(255, 193, 7, 0.5)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #FFC107, #FFD700)',
                  boxShadow: '0 6px 14px rgba(255, 193, 7, 0.7)',
                  transform: 'translateY(-2px)'
                },
                transition: 'all 0.2s',
                position: 'relative',
                overflow: 'hidden',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '200%',
                  height: '100%',
                  background: 'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.3) 50%, rgba(255,255,255,0) 100%)',
                  transform: 'translateX(-100%)',
                  transition: 'transform 0.6s ease',
                },
                '&:hover::after': {
                  transform: 'translateX(100%)'
                }
              }}
            >
              {homeContent.hero.button.text}
            </Button>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default DynamicHeroBanner; 